/* eslint-disable max-lines-per-function */
import '@divkitframework/divkit/dist/client.css?inline';

import { createDivkitModelCreatorByDefault } from '@mail/3promo/divkit';
import type { Divkit3promoData } from '@mail/3promo/types/divkit';
import { getPromoManagerSync, initDefaultPromoManager } from '@mail/3promo/utils';
import { coreLoggerDispatchError, coreLoggerDispatchKnownError } from '@mail-core/logger';
import { tryCatch } from '@mail-core/std/fn/sugar/tryCatch';
import { parseJSONSafe } from '@mail-core/std/fn/transform/parseJSONSafe';
import { truthy } from '@mail-core/std/fn/validate/truthy';
import type { ValueOrPromise } from '@mail-core/std/type';
import { getFeature } from 'Cloud/Application/FeaturesEs6';
import config from 'Cloud/config';
import { xray } from 'lib/xray';
import {
    IS_AUTO_TEST_HIDE,
    IS_AUTO_TEST_PROMO,
    IS_CORP_USER,
    IS_FAMILY_USER,
    IS_MOBILE_OR_TABLET_BROWSER,
    IS_VKT_DESKTOP,
    IS_VKT_WEBVIEW,
    IS_WEBVIEW,
} from 'reactApp/appHelpers/configHelpers';
import { features } from 'reactApp/mail-features';
import { removePromo } from 'reactApp/modules/promo/promo.module';
import { isPromoCanBeShown } from 'reactApp/modules/promo/promo.selectors';
import { EPromoType } from 'reactApp/modules/promo/promo.types';
import { advAucFullscreenCreator } from 'reactApp/modules/promoNew/items/advAucFullscreenCreator';
import { aiImproveViewerPromoPopupCreator } from 'reactApp/modules/promoNew/items/aiImproveViewerPromoPopupCreator';
import { aiImproveViewerPromoTooltipCreator } from 'reactApp/modules/promoNew/items/aiImproveViewerPromoTooltipCreator';
import { aiLetterStyleCreator } from 'reactApp/modules/promoNew/items/aiLetterStyleCreator';
import { aiPhotoImprovingBannerCreator } from 'reactApp/modules/promoNew/items/aiPhotoImprovingBannerCreator';
import { aiPhotoImprovingModalCreator } from 'reactApp/modules/promoNew/items/aiPhotoImprovingModalCreator';
import { aiRmvBannerCreator } from 'reactApp/modules/promoNew/items/aiRmvBannerCreator';
import { aiRmvHomePromoPopupCreator } from 'reactApp/modules/promoNew/items/aiRmvHomePromoPopupCreator';
import { aiRmvQuotaPromoPopupCreator } from 'reactApp/modules/promoNew/items/aiRmvQuotaPromoPopupCreator';
import { aiRmvViewerPromoPopupCreator } from 'reactApp/modules/promoNew/items/aiRmvViewerPromoPopupCreator';
import { aiRmvViewerPromoTooltipCreator } from 'reactApp/modules/promoNew/items/aiRmvViewerPromoTooltipCreator';
import { aiTariffPromoCreator } from 'reactApp/modules/promoNew/items/aiTariffPromoCreator';
import { aiUpgradeModalCreator2 } from 'reactApp/modules/promoNew/items/aiUpgradeModalCreator2';
import { blackFriday2025ModalCreator } from 'reactApp/modules/promoNew/items/blackFriday2025ModalCreator';
import { closeButtonExperimentModal } from 'reactApp/modules/promoNew/items/closeButtonExperimentModal';
import { duplicatesEntryPointModalCreator } from 'reactApp/modules/promoNew/items/duplicatesEntryPointModalCreator';
import { duplicatesOnboardingModalCreator } from 'reactApp/modules/promoNew/items/duplicatesOnboardingModalCreator';
import { composeFakeDoorCreator } from 'reactApp/modules/promoNew/items/fakedoorCreator';
import { familyAddButtonTooltipCreator } from 'reactApp/modules/promoNew/items/familyAddButtonTooltipCreator';
import { FamilyFreemiumFeaturesTooltipCreator } from 'reactApp/modules/promoNew/items/familyFreemiumFeaturesCreator';
import { FamilySettingsStackTooltipCreator } from 'reactApp/modules/promoNew/items/familySettingsStackCreator';
import { familySpaceToEachBalloon } from 'reactApp/modules/promoNew/items/familySpaceToEachBalloon';
import { familySpaceToEachModal } from 'reactApp/modules/promoNew/items/familySpaceToEachModal';
import { familyUpgradeModalCreator2 } from 'reactApp/modules/promoNew/items/familyUpgradeModalCreator2';
import { forWorkQuotaLandingPromoCreator } from 'reactApp/modules/promoNew/items/forWorkQuotaLandingPromoCreator';
import { forWorkUpgradeModalCreator } from 'reactApp/modules/promoNew/items/forWorkUpgradeModalCreator';
import { forWorkUpgradeModalCreator2 } from 'reactApp/modules/promoNew/items/forWorkUpgradeModalCreator2';
import { freemiumTestUnlimitedModalCreator } from 'reactApp/modules/promoNew/items/freemiumTestUnlimitedModalCreator';
import { logInAccountModalCreator } from 'reactApp/modules/promoNew/items/logInAccountModalCreator';
import { multiTariffSpaceToEach } from 'reactApp/modules/promoNew/items/multiTariffSpaceToEach';
import { neurofilterPromoCreator } from 'reactApp/modules/promoNew/items/neurofilterPromoCreator';
import { pdfAutosaveCreator } from 'reactApp/modules/promoNew/items/pdfAutosaveCreator';
import { pdfWorkWithPagesCreator } from 'reactApp/modules/promoNew/items/pdfWorkWithPagesCreator';
import { promoInWebviewPublicCreator } from 'reactApp/modules/promoNew/items/promoInWebviewPublicCreator';
import { publicAnimatedTrendModalCreator } from 'reactApp/modules/promoNew/items/publicAnimatedTrendModalCreator';
import { publicBenefitsOfAuthorizationPromoCreator } from 'reactApp/modules/promoNew/items/publicBenefitsOfAuthorizationPromoCreator';
import { unlimitedDocumentsAsidePromoCreator } from 'reactApp/modules/promoNew/items/unlimitedDocumentsAsidePromoCreator';
import { unlimitedDocumentsPromoCreator } from 'reactApp/modules/promoNew/items/unlimitedDocumentsPromoCreator';
import { vigboPrintBannerCreator } from 'reactApp/modules/promoNew/items/vigboPrintBannerCreator';
import { voluntarilyBAZPromo } from 'reactApp/modules/promoNew/items/voluntarilyBAZPromo';
import { defaultReactRenderDeps } from 'reactApp/modules/promoNew/render/react';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { safetyPersonalLocalStorage } from 'reactApp/storage';
import { store as reduxStore } from 'reactApp/store';
import { windowOpen } from 'reactApp/utils/opener';
import { isDarkThemeModeEnabled } from 'reactApp/utils/theme';

import style from './index.css';

type CustomConditionChecker = (data: Divkit3promoData) => ValueOrPromise<boolean>;
type PromoCondition = {
    check: CustomConditionChecker;
    defaultEnabled: boolean;
};
type DivkitCustomConditions = Partial<Record<string, PromoCondition>>;
type UrlPatternAwarePromoData = Divkit3promoData & { urlPatterns?: string[] };
const URL_CONDITION_KEY = 'URL_PATTERN_MATCH';

const shouldCheckByUrl = (data: Divkit3promoData): data is UrlPatternAwarePromoData =>
    Array.isArray(data.urlPatterns) && data.urlPatterns.length > 0;

const getUrlMatchResult = (data: Divkit3promoData): boolean => {
    return isUrlMatchesPatterns(data);
};

const wrapWithUrlCheck = (checker: CustomConditionChecker): CustomConditionChecker => {
    return (data) => {
        if (!shouldCheckByUrl(data)) {
            return checker(data);
        }

        return getUrlMatchResult(data) && checker(data);
    };
};

const DEFAULT_HOME_PATTERNS = [
    '^/home(/|$)',
    '^/gallery(/|$)',
    '^/albums(/|$)',
    '^/documents(/|$)',
    '^/family/folders(/|$)',
    '^/feed(/|$)',
    '^/shared/links(/|$)',
    '^/favorites(/|$)',
    '^/trashbin(/|$)',
    '^/attaches(/|$)',
];

const isUrlMatchesPatterns = (data: Divkit3promoData): boolean => {
    if (typeof window === 'undefined') {
        return false;
    }

    const { urlPatterns } = data as UrlPatternAwarePromoData;
    const hasOnlyCondition = data.condition?.mode === 'only' && data.condition.array.length > 0;

    let effectivePatterns: string[];

    if (hasOnlyCondition) {
        // при onlyCondition используем только указанные urlPatterns или дефолтные если их нет
        effectivePatterns = urlPatterns && urlPatterns.length > 0 ? urlPatterns : DEFAULT_HOME_PATTERNS;
    } else {
        // при addCondition: добавляем urlPatterns к DEFAULT_HOME_PATTERNS
        // DEFAULT_HOME_PATTERNS содержит основные страницы (home, gallery, documents и т.д.)
        // но не содержит например doc.mail.ru, promo/quota и другие страницы, где промки не должны показываться по умолчанию
        // поэтому можно безопасно добавлять к ним дополнительные urlPatterns
        effectivePatterns = urlPatterns && urlPatterns.length > 0 ? [...DEFAULT_HOME_PATTERNS, ...urlPatterns] : DEFAULT_HOME_PATTERNS;
    }

    const currentUrl = window.location.pathname + window.location.search;

    return effectivePatterns.some((pattern) => {
        try {
            return new RegExp(pattern).test(currentUrl);
        } catch (error) {
            return false;
        }
    });
};

const createCustomConditions = (): DivkitCustomConditions => {
    return {
        [URL_CONDITION_KEY]: {
            check: (data: Divkit3promoData) => {
                return isUrlMatchesPatterns(data);
            },
            defaultEnabled: true,
        },
        IS_PAID_USER: {
            check: wrapWithUrlCheck(() => {
                const state = reduxStore.getState();
                const isPaid = UserSelectors.isPaidUser(state);
                const isB2bBiz = UserSelectors.isB2BUser(state);
                return isPaid && !IS_CORP_USER && !isB2bBiz && !IS_FAMILY_USER;
            }),
            defaultEnabled: false,
        },
        IS_FREE_USER: {
            check: wrapWithUrlCheck(() => {
                const state = reduxStore.getState();
                const isPaid = UserSelectors.isPaidUser(state);
                const isB2bBiz = UserSelectors.isB2BUser(state);
                const quotaState = UserQuotaSelectors.getUserQuotaState(state as any);
                const isOverquota = Boolean(quotaState?.isOverQuota);
                return !isPaid && !IS_CORP_USER && !isB2bBiz && !IS_FAMILY_USER && !isOverquota;
            }),
            defaultEnabled: false,
        },
        IS_FAMILY_USER: {
            check: wrapWithUrlCheck(() => {
                return IS_FAMILY_USER;
            }),
            defaultEnabled: false,
        },
        IS_CORP_USER: {
            check: wrapWithUrlCheck(() => {
                return IS_CORP_USER;
            }),
            defaultEnabled: false,
        },
        IS_B2B_BIZ_USER: {
            check: wrapWithUrlCheck(() => {
                return UserSelectors.isB2BUser(reduxStore.getState());
            }),
            defaultEnabled: false,
        },
        IS_VKT_WEBVIEW: {
            check: wrapWithUrlCheck(() => {
                return IS_VKT_WEBVIEW;
            }),
            defaultEnabled: false,
        },
        IS_VKT_DESKTOP: {
            check: wrapWithUrlCheck(() => {
                return IS_VKT_DESKTOP;
            }),
            defaultEnabled: false,
        },
        IS_VKT: {
            check: wrapWithUrlCheck(() => {
                return IS_VKT_WEBVIEW || IS_VKT_DESKTOP;
            }),
            defaultEnabled: false,
        },
        IS_TOUCH: {
            check: wrapWithUrlCheck(() => {
                return IS_MOBILE_OR_TABLET_BROWSER;
            }),
            defaultEnabled: false,
        },
        IS_AUTO_TEST: {
            check: wrapWithUrlCheck(() => {
                return IS_AUTO_TEST_HIDE || IS_AUTO_TEST_PROMO;
            }),
            defaultEnabled: false,
        },
        IS_OVERQUOTA: {
            check: wrapWithUrlCheck(() => {
                const quotaState = UserQuotaSelectors.getUserQuotaState(reduxStore.getState() as any);
                return Boolean(quotaState?.isOverQuota);
            }),
            defaultEnabled: false,
        },
        IS_WEBVIEW: {
            check: wrapWithUrlCheck(() => {
                return Boolean(IS_WEBVIEW);
            }),
            defaultEnabled: false,
        },
    };
};

export const initPromoManager = () => {
    let manager = getPromoManagerSync();

    if (manager) {
        return manager;
    }
    manager = initDefaultPromoManager(
        {
            closeLimit: 3,
            showLimit: 10,
            showInterval: 30,
            showDelay: 600,
            promoTimeout: features.getAsDuration('promo-timeout', '10m'),
            promoDisabled: features.has('promo-disabled'),
            showId: features.getAsString('promo-force-show-id'),
            debugLog: features.has('3promo-debug'),
            ignoreShowInterval: features.has('ignore-show-interval'),
            ignoreGlobalShowInterval: features.has('ignore-global-show-interval'),
            ignoreShowLimit: features.has('ignore-show-limit'),
            override: {
                globalSettings: {
                    test: (resolve, reject) => {
                        const state = reduxStore.getState();
                        if (isPromoCanBeShown(state)) {
                            return resolve();
                        }

                        return reject();
                    },
                },
                models: {
                    base: {
                        callbacks: {
                            onHide: () => reduxStore.dispatch(removePromo(EPromoType.threePromo)),
                        },
                    },
                    notify: {
                        getNotifyContainer: () => document.querySelector('#notify-parent')!,
                    },
                },
            },
        },
        {
            features,
            stores: {
                /**
                 * Передается именно так, а не `localStorage: safetyPersonalLocalStorage`, т.к. эти методы не являются прямыми ключами,
                 * а оно не пробует достать, а перебирает доступные ключи объект. В теории - баг di-registry
                 */
                localStorage: {
                    getAsJSON: (key) => safetyPersonalLocalStorage.getAsJSON(key),
                    removeItem: (key) => safetyPersonalLocalStorage.removeItem(key),
                    setAsJSON: (key, value) => safetyPersonalLocalStorage.setAsJSON(key, value),
                },
            },
            xray,
            defaultReactRenderDeps,
        },
        [
            // Всегда оставлять в топе
            advAucFullscreenCreator,
            // -----------------------
            voluntarilyBAZPromo,
            aiRmvBannerCreator,
            aiRmvQuotaPromoPopupCreator,
            forWorkUpgradeModalCreator,
            forWorkUpgradeModalCreator2,
            aiUpgradeModalCreator2,
            familyUpgradeModalCreator2,
            aiRmvViewerPromoTooltipCreator,
            aiRmvViewerPromoPopupCreator,
            aiRmvHomePromoPopupCreator,
            freemiumTestUnlimitedModalCreator,
            aiLetterStyleCreator,
            aiPhotoImprovingModalCreator,
            aiPhotoImprovingBannerCreator,
            aiImproveViewerPromoPopupCreator,
            aiImproveViewerPromoTooltipCreator,
            aiTariffPromoCreator,
            neurofilterPromoCreator,
            logInAccountModalCreator,
            multiTariffSpaceToEach,
            familySpaceToEachBalloon,
            familySpaceToEachModal,
            closeButtonExperimentModal,
            FamilyFreemiumFeaturesTooltipCreator,
            blackFriday2025ModalCreator,
            forWorkQuotaLandingPromoCreator,
            publicBenefitsOfAuthorizationPromoCreator,
            publicAnimatedTrendModalCreator,
            promoInWebviewPublicCreator,
            unlimitedDocumentsPromoCreator,
            unlimitedDocumentsAsidePromoCreator,
            vigboPrintBannerCreator,
            pdfAutosaveCreator,
            pdfWorkWithPagesCreator,
            FamilySettingsStackTooltipCreator,
            composeFakeDoorCreator(),
            familyAddButtonTooltipCreator,
            duplicatesEntryPointModalCreator,
            duplicatesOnboardingModalCreator,
            ...tryCatch(
                () =>
                    createDivkitModelCreatorByDefault({
                        elementClass: style.divkit,
                        elementCss: {
                            hide: { classList: [style.divkitHide] },
                            show: {},
                        },
                        xraySend: (...args) => xray.send(...args),
                        windowOpen,
                        coreLoggerDispatchKnownError,
                        features3promo: {
                            getItem(key) {
                                const data = getFeature(key);
                                return typeof data === 'string' ? parseJSONSafe(data, data) : data;
                            },
                            keys() {
                                return config.get('FEATURES')?.enabled || [];
                            },
                        },
                        getTheme: () => (isDarkThemeModeEnabled() ? 'dark' : 'light'),
                        subscribeTheme: () => () => {}, // ?
                        addCustomParams: {
                            project: 'cloud',
                        },
                        addCustomConditions: createCustomConditions(),
                        // В этих "запрещённых" средах промка не показывается,
                        // пока соответствующее условие явно не выбрано в админке.
                        restrictedConditionKeys: ['IS_VKT', 'IS_TOUCH', 'IS_AUTO_TEST', 'IS_WEBVIEW'],
                    }),
                (error) => {
                    coreLoggerDispatchError(error, { location: 'divkit-model' });
                    return [];
                }
            ),
        ].filter(truthy)
    );

    // паузим менеджер
    manager.pause();

    return manager;
};
